import { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Briefcase, RefreshCw } from "lucide-react";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import Banner from "@/components/Banner";

export default function Designations() {
  const [items, setItems] = useState([]);
  const [deps, setDeps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", department_id: "", level: "" });
  const [err, setErr] = useState("");

  const depMap = Object.fromEntries(deps.map((d) => [d.id, d]));
  const enriched = items.map((d) => ({ ...d, _department_name: depMap[d.department_id]?.name || "" }));
  const { items: sortedItems, sortBy, sortDir, requestSort } = useSortableData(enriched, {
    initialKey: "title",
    accessors: { department: (r) => r._department_name },
  });

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/designations"),
      api.get("/departments"),
    ]);
    setItems(a.data);
    setDeps(b.data);
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/designations", form);
      setForm({ title: "", department_id: "", level: "" });
      setOpen(false);
      load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed");
    }
  };

  return (
    <AdminLayout title="Designations" breadcrumbs={["Home", "Organization", "Designations"]}>
      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">All Designations</h3>
          <div className="flex items-center gap-2">
            <button onClick={load} title="Refresh" data-testid="desig-refresh-btn" className="p-2 border border-slate-200 rounded hover:bg-slate-50 text-slate-600">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              data-testid="add-designation-btn"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 bg-[#089949] hover:bg-[#067A3A] text-white px-4 py-2 text-sm rounded font-medium"
            >
              <Plus className="h-4 w-4" /> Add Designation
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <SortableTH columnKey="title" active={sortBy} dir={sortDir} onClick={requestSort}>Title</SortableTH>
              <SortableTH columnKey="department" active={sortBy} dir={sortDir} onClick={requestSort}>Department</SortableTH>
              <SortableTH columnKey="level" active={sortBy} dir={sortDir} onClick={requestSort}>Level</SortableTH>
            </tr>
          </thead>
          <tbody>
            {sortedItems.length === 0 ? (
              <EmptyState colSpan={3} message="No designations yet" />
            ) : sortedItems.map((d) => (
              <tr key={d.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-[#226DB4]" /> {d.title}
                </td>
                <td className="px-4 py-3 text-slate-600">{d._department_name || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{d.level || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">New Designation</h3>
            <input
              required
              placeholder="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
              data-testid="desig-title-input"
            />
            <select
              required
              value={form.department_id}
              onChange={(e) => setForm({ ...form, department_id: e.target.value })}
              className="w-full border border-slate-200 rounded px-3 py-2 text-sm bg-white"
              data-testid="desig-dep-select"
            >
              <option value="">Select Department</option>
              {deps.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
            <input
              placeholder="Level (e.g. Junior, Mid, Senior)"
              value={form.level}
              onChange={(e) => setForm({ ...form, level: e.target.value })}
              className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
            />
            <Banner variant="error">{err}</Banner>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-slate-200 rounded">
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded" data-testid="desig-save-btn">
                Save
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
